import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type PaymentType = 'blik' | 'other';

interface ConfirmPurchaseDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (paymentType: PaymentType, blikCode?: string) => Promise<void>
  amount: number
  isLoading: boolean
}

export function ConfirmPurchaseDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  amount,
  isLoading 
}: ConfirmPurchaseDialogProps) {
  const { t } = useTranslation()
  const [blikCode, setBlikCode] = useState<string>('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentType | null>(null)
  /*
    Zgoda na rozpoczecie swiadczenia PRZED uplywem terminu odstapienia. Kredyty to tresc
    cyfrowa: bez tej zgody i bez pouczenia o utracie prawa odstapienia konsument zachowuje je
    przez 12 miesiecy (art. 38 pkt 13 w zw. z art. 21 ust. 2 ustawy o prawach konsumenta),
    czyli moglby przez rok zadac zwrotu za zuzyte kredyty. Domyslnie NIEZAZNACZONE — zgoda
    zaznaczona z gory nie jest zgoda.
  */
  const [withdrawalConsent, setWithdrawalConsent] = useState(false)

  // Reset form when dialog is opened/closed
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleBlikCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow 6 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setBlikCode(value)
  }

  const handleConfirm = async () => {
    if (!paymentMethod || !withdrawalConsent) return
    if (paymentMethod === 'blik') {
      if (blikCode.length !== 6) return
      await onConfirm(paymentMethod, blikCode)
    } else {
      await onConfirm(paymentMethod)
    }
  }

  const resetState = () => {
    setBlikCode('')
    setPaymentMethod(null)
    setWithdrawalConsent(false)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Potwierdź zakup</DialogTitle>
          <DialogDescription>
            Czy na pewno chcesz kupić {amount} kredytów za {amount} PLN?
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {!paymentMethod ? (
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => setPaymentMethod('blik')}
                variant="outline"
                className="w-full h-24 flex flex-col items-center justify-center gap-2"
              >
                <span className="text-lg font-bold">BLIK</span>
                <span className="text-sm text-muted-foreground">Szybka płatność</span>
              </Button>
              <Button
                onClick={() => setPaymentMethod('other')}
                variant="outline"
                className="w-full h-24 flex flex-col items-center justify-center gap-2"
              >
                <span className="text-lg font-bold">Inna płatność</span>
                <span className="text-sm text-muted-foreground">Przelew, karta</span>
              </Button>
            </div>
          ) : paymentMethod === 'blik' ? (
            <div className="space-y-2">
              <Label htmlFor="blik-code">Kod BLIK</Label>
              <Input
                id="blik-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Wpisz 6-cyfrowy kod"
                value={blikCode}
                onChange={handleBlikCodeChange}
                className="text-center text-2xl tracking-widest"
                disabled={isLoading}
                autoFocus
              />
            </div>
          ) : null}
        </div>

        {paymentMethod && (
          <div className="flex items-start gap-2 rounded-md bg-muted/50 p-3">
            <Checkbox
              id="withdrawal-consent"
              checked={withdrawalConsent}
              onCheckedChange={(checked) => setWithdrawalConsent(checked === true)}
              disabled={isLoading}
              data-testid="withdrawal-consent"
            />
            <Label htmlFor="withdrawal-consent" className="text-xs font-normal leading-snug text-muted-foreground">
              {t('purchase-withdrawal-consent')}{' '}
              <Link to="/terms" target="_blank" className="underline hover:text-foreground">
                {t('purchase-withdrawal-consent-terms-link')}
              </Link>
            </Label>
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Anuluj
          </Button>
          {paymentMethod && (
            <Button 
              data-testid="confirm-purchase"
              onClick={handleConfirm} 
              disabled={isLoading || !withdrawalConsent || (paymentMethod === 'blik' && blikCode.length !== 6)}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Przetwarzanie...
                </>
              ) : (
                'Zapłać'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 